import { useEffect, useState } from 'react';
import type { EdrModuleId } from 'src/mindtime/constants/edrSubscriptionModules';
import logger from 'src/utils/logger';
import { useEdrDataGate } from './useEdrDataGate';

type UseEdrDetailRecordOptions<T> = {
  moduleId: EdrModuleId;
  recordId: string;
  initialRecord?: T | null;
  getPreview: (id: string) => T | null;
  fetchLive: (id: string) => Promise<T | null>;
  logLabel: string;
};

export const useEdrDetailRecord = <T>({
  moduleId,
  recordId,
  initialRecord = null,
  getPreview,
  fetchLive,
  logLabel,
}: UseEdrDetailRecordOptions<T>): {
  record: T | null;
  loading: boolean;
  notFound: boolean;
} => {
  const { wait, isPreviewMode } = useEdrDataGate(moduleId);
  const [record, setRecord] = useState<T | null>(initialRecord);
  const [loading, setLoading] = useState(!initialRecord);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!recordId) {
      setRecord(null);
      setLoading(false);
      setNotFound(true);
      return undefined;
    }

    if (wait) {
      setLoading(!initialRecord);
      return undefined;
    }

    if (isPreviewMode) {
      const preview = getPreview(recordId);
      setRecord(preview);
      setLoading(false);
      setNotFound(!preview);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setNotFound(false);

    fetchLive(recordId)
      .then((data) => {
        if (cancelled) return;
        setRecord(data);
        setNotFound(!data);
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', logLabel);
          setRecord(null);
          setNotFound(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // getPreview/fetchLive are stable module-level refs from callers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordId, wait, isPreviewMode, initialRecord]);

  return { record, loading, notFound };
};
